/**
 * `<player-avatar>`: who sits in a seat - name, team, whose turn it is, and
 * whether a person or a bot is playing it - with a slot beside the avatar for
 * the player's face-down Foot.
 *
 * The Foot is slotted in rather than placed by the seat: it belongs to the
 * player, not to the table, and riding alongside the avatar means it picks up
 * the avatar's alignment instead of needing the seat's orientation again.
 */

import type { TeamColor } from "../../server/protocol.js";
import { h, setAttributes } from "../dom.js";
import type { SeatHolder } from "../lobby.js";
import { BaseElement } from "./base-element.js";
import styles from "./player-avatar.css";

export type AvatarAlign = "left" | "right";

const SVG_NS = "http://www.w3.org/2000/svg";

/** The chip beside the name; a person playing their own seat needs none. */
const HOLDER_CHIPS: Readonly<Record<SeatHolder, string>> = {
    away: "Away",
    bot: "Bot",
    player: ""
};

function createIcon(shapes: readonly (readonly [string, Readonly<Record<string, string>>])[]): SVGSVGElement {
    const svg = document.createElementNS(SVG_NS, "svg");

    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("aria-hidden", "true");

    for (const [tag, attributes] of shapes) {
        const shape = document.createElementNS(SVG_NS, tag);

        for (const [name, value] of Object.entries(attributes)) {
            shape.setAttribute(name, value);
        }

        svg.append(shape);
    }

    return svg;
}

function createSilhouette(): SVGSVGElement {
    return createIcon([
        ["circle", { cx: "12", cy: "8", r: "4" }],
        ["path", { d: "M12 14c-4.4 0-8 2.7-8 6v1h16v-1c0-3.3-3.6-6-8-6z" }]
    ]);
}

/** A boxy head with an antenna, so a bot reads as one at a glance. */
function createRobot(): SVGSVGElement {
    return createIcon([
        ["circle", { cx: "12", cy: "3", r: "1.5" }],
        ["rect", { x: "11.25", y: "3", width: "1.5", height: "3" }],
        ["path", { "fill-rule": "evenodd", "d": "M6 6h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2zm2.5 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm7 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM9 15.5h6v1.5H9z" }],
        ["rect", { x: "1.5", y: "10", width: "2", height: "4", rx: "1" }],
        ["rect", { x: "20.5", y: "10", width: "2", height: "4", rx: "1" }]
    ]);
}

export class PlayerAvatar extends BaseElement {
    #name = "";

    #color: TeamColor = "blue";

    #active = false;

    #dealer = false;

    #align: AvatarAlign = "left";

    #status = "";

    #holder: SeatHolder = "player";

    #callout = "";

    readonly #nameText = h("span", { class: "given" });

    readonly #tagText = h("span", { class: "tag" });

    readonly #holderChip = h("span", { class: "holder" });

    readonly #dealerChip = h("span", { class: "dealer", title: "Dealer" }, "D");

    readonly #face = h("div", { class: "avatar" });

    readonly #statusText = h("span", { class: "status" });

    #calloutText = h("span", { class: "callout", hidden: true });

    constructor() {
        super(styles);

        this.root.append(h(
            "div",
            { "class": "avatar-block", "aria-hidden": "true" },
            h("span", { class: "name" }, h("span", { class: "plate" }, this.#nameText, this.#tagText), this.#holderChip, this.#dealerChip),
            h("div", { class: "icon-row" }, this.#face, h("slot", {})),
            this.#statusText,
            this.#calloutText
        ));
    }

    /** A display name such as "PurplePanda#2953"; the number is drawn quieter. */
    set name(value: string) {
        this.#name = value;
        this.invalidate();
    }

    set color(value: TeamColor) {
        this.#color = value;
        this.invalidate();
    }

    set active(value: boolean) {
        this.#active = value;
        this.invalidate();
    }

    set dealer(value: boolean) {
        this.#dealer = value;
        this.invalidate();
    }

    set align(value: AvatarAlign) {
        this.#align = value;
        this.invalidate();
    }

    /** The short line under the avatar: what they hold, or that it is their turn. */
    set status(value: string) {
        this.#status = value;
        this.invalidate();
    }

    /** Who is playing the seat, which decides the face and the chip beside the name. */
    set holder(value: SeatHolder) {
        this.#holder = value;
        this.invalidate();
    }

    /** A passing note about the seat - someone joining or leaving - or empty for none. */
    set callout(value: string) {
        this.#callout = value;
        this.invalidate();
    }

    protected render(): void {
        const hash = this.#name.lastIndexOf("#");
        const automated = this.#holder !== "player";

        setAttributes(this, { active: this.#active, align: this.#align, holder: this.#holder });
        this.style.setProperty("--team", `var(--team-${this.#color})`);

        this.#nameText.textContent = hash === -1 ? this.#name : this.#name.slice(0, hash);
        this.#tagText.textContent = hash === -1 ? "" : this.#name.slice(hash);
        this.#holderChip.textContent = HOLDER_CHIPS[this.#holder];
        this.#holderChip.hidden = this.#holder === "player";
        this.#dealerChip.hidden = !this.#dealer;
        this.#statusText.textContent = this.#status;

        // Swapped only when it changes, so the face is not rebuilt every update.
        if (this.#face.dataset.face !== String(automated)) {
            this.#face.dataset.face = String(automated);
            this.#face.replaceChildren(automated ? createRobot() : createSilhouette());
        }

        // A new note restarts its fade by being a new element.
        if (this.#calloutText.textContent !== this.#callout) {
            const next = h("span", { class: "callout", hidden: this.#callout === "" }, this.#callout);

            this.#calloutText.replaceWith(next);
            this.#calloutText = next;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "player-avatar": PlayerAvatar;
    }
}

customElements.define("player-avatar", PlayerAvatar);
